import { isNumber } from "lodash-es";
import { forwardRef } from "react";
import { useController } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { t } from "i18n/i18n";
import { SquareIconButton } from "mds/components/buttons/SquareIconButton";
import { AddPlusIcon, SubtractMinusIcon } from "mds/icons";

type NumberFieldWithStepperButtonsProps = {
  className?: string;
  name: string;
  label?: string;
  disabled?: boolean;
  // set the `progressive` option on the useForm hook to have RHF pass these automatically
  max?: string | number;
  min?: string | number;
};

export const NumberFieldWithStepperButtons = forwardRef<
  HTMLInputElement,
  NumberFieldWithStepperButtonsProps
>(function NumberFieldWithStepperButtonsInternal({ className, label, ...props }, ref) {
  const {
    field,
    fieldState: { error },
  } = useController(props);
  const disabled = props.disabled;
  const value = parseInt(field.value as string, 10);
  const max = props.max !== undefined ? Number(props.max) : undefined;
  const min = props.min !== undefined ? Number(props.min) : undefined;
  const isNaNValue = Number.isNaN(value);

  return (
    <div
      className={twMerge("group flex h-[32px] items-stretch gap-2", className)}
      data-disabled={disabled}
      role="spinbutton"
    >
      <div className="order-2 flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-black-tint-90 bg-white p-2 text-sm font-medium text-black-tint-10 group-data-[disabled]:cursor-not-allowed group-data-[disabled]:bg-black-tint-93 group-data-[disabled]:text-black-tint-70">
        <input
          aria-invalid={error ? "true" : "false"}
          className="w-6 text-right"
          inputMode="numeric"
          pattern="[0-9]*"
          type="text"
          onKeyDown={(e) => {
            if (e.key === "ArrowUp") {
              e.preventDefault();
              if (!disabled && !isNaNValue && (!isNumber(max) || value < max)) {
                field.onChange(value + 1);
              }
            }
            if (e.key === "ArrowDown") {
              e.preventDefault();
              if (!disabled && !isNaNValue && (!isNumber(min) || value > min)) {
                field.onChange(value - 1);
              }
            }
          }}
          {...field}
          ref={ref}
        />
        {label && <span className="select-none">{label}</span>}
      </div>

      <SquareIconButton
        aria-label={t("common.decrement")}
        className="order-1"
        disabled={disabled || isNaNValue || (isNumber(min) && value <= min)}
        kind="tertiary"
        size="s"
        onClick={() => field.onChange(value - 1)}
      >
        <SubtractMinusIcon />
      </SquareIconButton>

      <SquareIconButton
        aria-label={t("common.increment")}
        className="order-3"
        disabled={disabled || isNaNValue || (isNumber(max) && value >= max)}
        kind="tertiary"
        size="s"
        onClick={() => field.onChange(value + 1)}
      >
        <AddPlusIcon />
      </SquareIconButton>
    </div>
  );
});
